/**
 * CSSAuditor.ts - Audit CSS files for hardcoded values that should use skin variables
 * 
 * Scans package CSS files to identify hardcoded colors, sizes, and other values
 * that should be replaced with skin variables for consistent theming.
 */

export interface CSSAuditIssue {
  file: string;
  line: number;
  property: string;
  value: string;
  suggestedVariable: string;
  severity: 'error' | 'warning' | 'info';
  category: 'color' | 'size' | 'spacing' | 'typography' | 'other';
}

export interface CSSAuditReport {
  totalIssues: number;
  byCategory: Record<string, number>;
  bySeverity: Record<string, number>;
  byPackage: Record<string, CSSAuditIssue[]>;
  issues: CSSAuditIssue[];
}

/**
 * Patterns for detecting hardcoded values
 */
const PATTERNS = {
  // Color patterns
  hexColor: /#[0-9A-Fa-f]{3,8}/g,
  rgbColor: /rgba?\([^)]+\)/g,
  hslColor: /hsla?\([^)]+\)/g,
  
  // Size patterns
  pixelSize: /\d+px/g,
  remSize: /\d+(\.\d+)?rem/g,
  
  // Named colors (should avoid these)
  namedColors: /\b(white|black|red|blue|green|yellow|orange|purple|pink|gray|grey)\b/gi,
};

/**
 * Map common CSS properties to suggested skin variables
 */
const PROPERTY_TO_VARIABLE_MAP: Record<string, string[]> = {
  'background': ['--surface-base', '--surface-raised', '--card-bg'],
  'background-color': ['--surface-base', '--surface-raised', '--card-bg'],
  'color': ['--text-primary', '--text-secondary', '--text-tertiary'],
  'border-color': ['--border-default', '--border-subtle', '--border-strong'],
  'border': ['--border-default'],
  'box-shadow': ['--shadow-sm', '--shadow-md', '--shadow-lg'],
  'padding': ['--spacing-sm', '--spacing-md', '--spacing-lg'],
  'margin': ['--spacing-sm', '--spacing-md', '--spacing-lg'],
  'gap': ['--spacing-sm', '--spacing-md', '--spacing-lg'],
  'font-size': ['--font-size-sm', '--font-size-base', '--font-size-lg'],
  'font-family': ['--font-family', '--font-family-mono'],
  'border-radius': ['--border-radius-sm', '--border-radius-md', '--border-radius-lg'],
};

/**
 * Suggest appropriate skin variable based on property and value
 */
function suggestVariable(property: string, value: string): string {
  const suggestions = PROPERTY_TO_VARIABLE_MAP[property] || [];
  
  // Color-specific suggestions
  if (value.match(PATTERNS.hexColor) || value.match(PATTERNS.rgbColor)) {
    if (property.includes('background') || property.includes('bg')) {
      if (value.includes('ffffff') || value.includes('255, 255, 255')) {
        return '--surface-base';
      }
      if (value.includes('f8f9fa') || value.includes('f9fafb')) {
        return '--surface-raised';
      }
      return '--surface-base';
    }
    if (property.includes('color') && !property.includes('background')) {
      if (value.includes('212529') || value.includes('1f2937')) {
        return '--text-primary';
      }
      if (value.includes('6c757d') || value.includes('6b7280')) {
        return '--text-secondary';
      }
      return '--text-primary';
    }
    if (property.includes('border')) {
      return '--border-default';
    }
  }
  
  // Return first suggestion or generic
  return suggestions[0] || `--${property.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
}

/**
 * Determine severity based on property and context
 */
function determineSeverity(property: string, value: string): 'error' | 'warning' | 'info' {
  // Critical: Colors that affect readability
  if ((property === 'color' || property === 'background-color') && 
      !value.includes('var(')) {
    return 'error';
  }
  
  // Warning: Interactive elements
  if (property.includes('hover') || property.includes('active') || 
      property.includes('focus') || property.includes('selected')) {
    return 'warning';
  }
  
  // Info: Spacing and sizing
  if (property.includes('padding') || property.includes('margin') || 
      property.includes('gap') || property.includes('size')) {
    return 'info';
  }
  
  return 'warning';
}

/**
 * Determine category based on property
 */
function determineCategory(property: string): CSSAuditIssue['category'] {
  if (property.includes('color') || property.includes('background') || property.includes('bg')) {
    return 'color';
  }
  if (property.includes('width') || property.includes('height')) {
    return 'size';
  }
  if (property.includes('padding') || property.includes('margin') || property.includes('gap')) {
    return 'spacing';
  }
  if (property.includes('font') || property.includes('line-height')) {
    return 'typography';
  }
  return 'other';
}

/**
 * Parse CSS content and find hardcoded values
 */
export function auditCSSContent(
  content: string,
  filePath: string
): CSSAuditIssue[] {
  const issues: CSSAuditIssue[] = [];
  const lines = content.split('\n');
  
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Skip comments and empty lines
    if (trimmed.startsWith('/*') || trimmed.startsWith('//') || !trimmed) {
      return;
    }
    
    // Skip lines that already use CSS variables
    if (trimmed.includes('var(--')) {
      return;
    }
    
    // Parse CSS property: value pairs
    const match = trimmed.match(/([a-z-]+)\s*:\s*([^;]+);?/i);
    if (!match) return;
    
    const [, property, value] = match;
    
    // Check for hardcoded colors
    if (value.match(PATTERNS.hexColor) || 
        value.match(PATTERNS.rgbColor) || 
        value.match(PATTERNS.hslColor) ||
        value.match(PATTERNS.namedColors)) {
      
      issues.push({
        file: filePath,
        line: index + 1,
        property,
        value: value.trim(),
        suggestedVariable: suggestVariable(property, value),
        severity: determineSeverity(property, value),
        category: determineCategory(property)
      });
    }
    
    // Check for hardcoded sizes (only if not 0)
    if (value !== '0' && (value.match(PATTERNS.pixelSize) || value.match(PATTERNS.remSize))) {
      if (property.includes('padding') || property.includes('margin') || 
          property.includes('gap') || property.includes('spacing')) {
        issues.push({
          file: filePath,
          line: index + 1,
          property,
          value: value.trim(),
          suggestedVariable: suggestVariable(property, value),
          severity: 'info',
          category: determineCategory(property)
        });
      }
    }
  });
  
  return issues;
}

/**
 * Generate audit report from multiple files
 */
export function generateAuditReport(
  fileContents: Map<string, string>
): CSSAuditReport {
  const allIssues: CSSAuditIssue[] = [];
  const byPackage: Record<string, CSSAuditIssue[]> = {};
  
  fileContents.forEach((content, filePath) => {
    const issues = auditCSSContent(content, filePath);
    allIssues.push(...issues);
    
    // Extract package name from path
    const packageMatch = filePath.match(/packages\/([^/]+)/);
    const packageName = packageMatch ? packageMatch[1] : 'unknown';
    
    if (!byPackage[packageName]) {
      byPackage[packageName] = [];
    }
    byPackage[packageName].push(...issues);
  });
  
  // Count by category
  const byCategory: Record<string, number> = {};
  const bySeverity: Record<string, number> = {};
  
  allIssues.forEach(issue => {
    byCategory[issue.category] = (byCategory[issue.category] || 0) + 1;
    bySeverity[issue.severity] = (bySeverity[issue.severity] || 0) + 1;
  });
  
  return {
    totalIssues: allIssues.length,
    byCategory,
    bySeverity,
    byPackage,
    issues: allIssues
  };
}

/**
 * Format audit report as readable text
 */
export function formatAuditReport(report: CSSAuditReport): string {
  const lines: string[] = [];
  
  lines.push('='.repeat(70));
  lines.push('CSS AUDIT REPORT');
  lines.push('='.repeat(70));
  lines.push('');
  
  lines.push(`Total Issues: ${report.totalIssues}`);
  lines.push('');
  
  lines.push('By Severity:');
  Object.entries(report.bySeverity).forEach(([severity, count]) => {
    const icon = severity === 'error' ? '❌' : severity === 'warning' ? '⚠️' : 'ℹ️';
    lines.push(`  ${icon} ${severity}: ${count}`);
  });
  lines.push('');
  
  lines.push('By Category:');
  Object.entries(report.byCategory).forEach(([category, count]) => {
    lines.push(`  - ${category}: ${count}`);
  });
  lines.push('');
  
  lines.push('By Package:');
  Object.entries(report.byPackage)
    .sort(([, a], [, b]) => b.length - a.length)
    .forEach(([pkg, issues]) => {
      lines.push(`  📦 ${pkg}: ${issues.length} issues`);
    });
  lines.push('');
  
  lines.push('Top Issues (first 20):');
  report.issues.slice(0, 20).forEach(issue => {
    const icon = issue.severity === 'error' ? '❌' : issue.severity === 'warning' ? '⚠️' : 'ℹ️';
    lines.push(`  ${icon} ${issue.file}:${issue.line}`);
    lines.push(`     ${issue.property}: ${issue.value}`);
    lines.push(`     → Suggest: ${issue.suggestedVariable}`);
    lines.push('');
  });
  
  lines.push('='.repeat(70));
  
  return lines.join('\n');
}

/**
 * Generate fix suggestions for a file
 */
export function generateFixSuggestions(issues: CSSAuditIssue[]): string {
  const lines: string[] = [];
  
  lines.push('/* Suggested CSS Variable Replacements */');
  lines.push('');
  
  issues.forEach(issue => {
    lines.push(`/* Line ${issue.line}: ${issue.property} */`);
    lines.push(`/* OLD: ${issue.property}: ${issue.value}; */`);
    lines.push(`${issue.property}: var(${issue.suggestedVariable});`);
    lines.push('');
  });
  
  return lines.join('\n');
}
